import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
    <div className="text-gray-400 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

const TermsPage = () => (
  <div className="min-h-screen bg-gray-950 pt-24 pb-20 px-4">
    <div className="max-w-3xl mx-auto">
      <div className="mb-12">
        <div className="text-green-400 font-mono text-sm mb-3">// legal</div>
        <h1 className="text-4xl font-black text-white mb-3">Terms of Service</h1>
        <p className="text-gray-500 text-sm">Last updated: June 19, 2026</p>
      </div>

      <Section title="1. Who we are">
        <p>
          PlarixMC ("we", "us") provides Minecraft game server hosting through plarixmc.com.
          By creating an account or purchasing a service, you agree to these Terms. If you do
          not agree, please do not use the service.
        </p>
      </Section>

      <Section title="2. Your account">
        <p>
          You must provide accurate information when registering and keep your login
          credentials secure. You are responsible for all activity that happens under your
          account. You must be at least 16 years old, or have the consent of a parent or
          guardian, to purchase services.
        </p>
      </Section>

      <Section title="3. The service">
        <p>
          We provide virtual Minecraft server instances with the resources (RAM, storage,
          player slots) described on the plan you purchase. Servers are typically deployed
          within minutes of a successful payment. We target 99.9% uptime but do not guarantee
          uninterrupted availability — maintenance, upstream network issues, and events outside
          our control can cause downtime. Planned maintenance is announced on our{' '}
          <a href="https://status.plarixmc.com" className="text-green-400 hover:text-green-300">status page</a>.
        </p>
        <p>
          Minecraft is a trademark of Mojang AB / Microsoft. PlarixMC is not affiliated with,
          endorsed by, or associated with Mojang or Microsoft.
        </p>
      </Section>

      <Section title="4. Billing and renewals">
        <p>
          Services are billed in advance on a recurring cycle (monthly unless stated
          otherwise), in EUR. Payments are processed by Stripe and PayPal; you can also pay
          from your PlarixMC account balance. Where auto-renewal from balance is enabled, the
          renewal amount is deducted automatically on the due date.
        </p>
        <p>
          Plan upgrades and downgrades are prorated: you pay (or are credited) only the price
          difference for the days remaining in your current billing cycle.
        </p>
      </Section>

      <Section title="5. Late payment, suspension and deletion">
        <p>
          We send payment reminders before your due date. If a renewal payment is not received,
          your server may be suspended. If payment is still not received, the server and its
          data may be permanently deleted 14 days after the due date. We recommend downloading
          backups of anything important before letting a service lapse.
        </p>
      </Section>

      <Section title="6. Refunds">
        <p>
          By placing an order you request that the service starts immediately. New server
          orders are eligible for a full refund within 48 hours of purchase if you are not
          satisfied — contact support. After 48 hours, and for renewal payments, refunds are
          issued at our discretion, normally as account credit. Account balance top-ups are
          non-refundable except where required by law. Nothing in this section limits rights
          you have under applicable consumer protection law.
        </p>
      </Section>

      <Section title="7. Acceptable use">
        <p>You agree not to use the service to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>host or distribute illegal content, malware, or stolen data;</li>
          <li>attack, scan, or disrupt other systems or networks (including DDoS);</li>
          <li>run crypto miners, proxies/VPN exits, or anything other than Minecraft-related workloads;</li>
          <li>infringe intellectual property rights, including selling access to pirated plugins or mods;</li>
          <li>harass, threaten, or harm others.</li>
        </ul>
        <p>
          We may suspend or terminate services that violate this section, that endanger the
          stability or security of our infrastructure, or that we are required to remove by law.
          Where reasonable, we will warn you first.
        </p>
      </Section>

      <Section title="8. Your content and backups">
        <p>
          You retain all rights to worlds, configurations, and files you upload. You grant us
          the technical permissions needed to store and run them. You are responsible for
          keeping your own copies of important data; backup features are provided as-is and
          are not a substitute for your own backups.
        </p>
      </Section>

      <Section title="9. Liability">
        <p>
          To the maximum extent permitted by law, our total liability for any claim related to
          the service is limited to the amount you paid us in the 3 months before the event
          giving rise to the claim. We are not liable for indirect damages such as lost
          profits, lost data, or lost playtime. Nothing in these Terms excludes liability that
          cannot be excluded under applicable law.
        </p>
      </Section>

      <Section title="10. Changes to these Terms">
        <p>
          We may update these Terms from time to time. If a change is material, we will notify
          you by email or a notice on the site before it takes effect. Continuing to use the
          service after a change takes effect means you accept the updated Terms.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          Questions about these Terms: <a href="mailto:info@plarixmc.com" className="text-green-400 hover:text-green-300">info@plarixmc.com</a>{' '}
          or open a <Link to="/tickets" className="text-green-400 hover:text-green-300">support ticket</Link>.
        </p>
      </Section>
    </div>
  </div>
);

export default TermsPage;
